import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import RadioGroup from '@amzn/awsui-components-core/radio-group';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React, { useEffect, useState } from 'react';

export interface SubsetSaveAsDialogProps {
  readonly visible: boolean;
  /** Rows that would be saved. Not displayed — it only gates Save, since a view of nothing is not a view. */
  readonly rowCount: number;
  /**
   * The view being overwritten, when `Save` opened this over an existing one — its name and settings seed the
   * form, so Save is a chance to adjust them rather than a blind overwrite. Absent for `Save As`.
   */
  readonly existing?: { readonly view_name: string; readonly visibility: 'PRIVATE' | 'PUBLIC'; readonly is_default: boolean };
  readonly onDismiss: () => void;
  /** Rejects with a message the dialog shows inline (a duplicate name comes back as 409). */
  readonly onSave: (name: string, visibility: 'PRIVATE' | 'PUBLIC', isDefault: boolean) => Promise<void>;
  readonly testId?: string;
}

/**
 * Prophecy's `Subset Save As` / `Save`, minus the `Static` checkbox.
 *
 * One dialog for both, because `Save` is not a silent overwrite: it reopens prefilled so the name, visibility
 * and default can be adjusted on the way through. With no `existing`, it creates.
 *
 * There is no static/dynamic split here — a view is always its resolved rows — so offering the checkbox would
 * promise a distinction nothing downstream makes.
 *
 * Saving is awaited rather than fire-and-forget: a duplicate name is a 409 the user can fix by typing, so the
 * dialog stays open and says so instead of closing over a failure.
 */
const SubsetSaveAsDialog: React.FC<SubsetSaveAsDialogProps> = ({ visible, rowCount, existing, onDismiss, onSave, testId }) => {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'PRIVATE' | 'PUBLIC'>('PRIVATE');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Seed the form each time it OPENS, not once per mount: the same dialog serves `Save` (prefilled from the
   * view being overwritten) and `Save As` (blank), and it stays mounted between the two.
   */
  useEffect(() => {
    if (!visible) return;
    setName(existing?.view_name ?? '');
    setVisibility(existing?.visibility ?? 'PRIVATE');
    setIsDefault(existing?.is_default ?? false);
    setError(null);
  }, [visible, existing?.view_name, existing?.visibility, existing?.is_default]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onSave(name.trim(), visibility, isDefault);
      // No reset here: the form is re-seeded on open, and a rejected save must keep what was typed.
      onDismiss();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save the view.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="small"
      header={existing === undefined ? 'Subset Save As' : 'Save Subset'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} data-testid={testId ? `${testId}-cancel` : undefined}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saving}
              // A view with no name cannot be found again, and one with no rows is not a view.
              disabled={name.trim().length === 0 || rowCount === 0}
              onClick={() => void save()}
              data-testid={testId ? `${testId}-save` : undefined}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
      data-testid={testId}
    >
      <SpaceBetween size="m">
        {error !== null && (
          <Alert type="error" data-testid={testId ? `${testId}-error` : undefined}>
            {error}
          </Alert>
        )}
        <FormField label="Name">
          <Input
            value={name}
            onChange={({ detail }) => setName(detail.value)}
            placeholder="Name this view"
            ariaLabel="View name"
            data-testid={testId ? `${testId}-name` : undefined}
          />
        </FormField>
        <RadioGroup
          value={visibility}
          onChange={({ detail }) => setVisibility(detail.value as 'PRIVATE' | 'PUBLIC')}
          items={[
            { value: 'PRIVATE', label: 'Private', description: 'Only you see it.' },
            { value: 'PUBLIC', label: 'Public', description: 'Everyone browsing this hierarchy sees it.' }
          ]}
          data-testid={testId ? `${testId}-visibility` : undefined}
        />
        <Checkbox checked={isDefault} onChange={({ detail }) => setIsDefault(detail.checked)} data-testid={testId ? `${testId}-default` : undefined}>
          {/* One default per person per hierarchy, and one public default — saving a new one replaces it. */}
          Default for this hierarchy
        </Checkbox>
      </SpaceBetween>
    </Modal>
  );
};

export default SubsetSaveAsDialog;
